import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useOffers } from "@/hooks/use-offers-queries";
import { FALLBACK_WEIGHTS, toPipelineOffer, useOfferPipelineWeights } from "@/hooks/use-offer-pipeline";
import { nextCloseDate } from "@/lib/customer-kpi-calc";
import { calcOfferTotals, type OfferLineForCalc } from "@/lib/offer-calc";
import { weightedPipeline, type WeightedPipelineResult } from "@/lib/offer-pipeline-calc";

type GeekLine = OfferLineForCalc & { offer_id: string };

const LINE_COLUMNS = "offer_id, phase, cost_type, pricing_type, quantity, unit, unit_price, purchase_price, amount, overtime_split";

export interface CustomerOfferGeek {
  /** Vektet GEEK estimert på kundens sendte, gyldige tilbud (rawKr = uvektet). */
  value: WeightedPipelineResult;
  offerCount: number;
  nextCloseDate: string | null;
  isLoading: boolean;
}

/**
 * «GEEK i pipe nå» på kundekortet (v3): GEEK estimert per sendt tilbud
 * (`calcOfferTotals().bf`, samme tall som tilbudseditorens footer) vektet med
 * tilbudets sannsynlighet via `weightedPipeline`. GEEK per tilbud finnes ikke
 * som kolonne, så linjene hentes for kundens sendte tilbud; spørringen er
 * bundet på tilbuds-id-ene.
 */
export function useCustomerOfferGeek(customerId: string | undefined): CustomerOfferGeek {
  const { data: offers = [], isLoading: offersLoading } = useOffers();
  const { data: weights = FALLBACK_WEIGHTS } = useOfferPipelineWeights();

  const sent = useMemo(
    () => offers.filter((o) => o.customer_id === customerId && o.status === "sent"),
    [offers, customerId],
  );
  const ids = useMemo(() => sent.map((o) => o.id).sort(), [sent]);

  const { data: lines = [], isLoading: linesLoading } = useQuery({
    queryKey: ["customer-offer-geek", customerId, ids],
    queryFn: async (): Promise<GeekLine[]> => {
      const { data, error } = await supabase.from("offer_lines").select(LINE_COLUMNS).in("offer_id", ids);
      if (error) throw error;
      return (data ?? []) as unknown as GeekLine[];
    },
    enabled: !!customerId && ids.length > 0,
    staleTime: 60 * 1000,
  });

  return useMemo(() => {
    const byOffer = new Map<string, GeekLine[]>();
    for (const l of lines) {
      const bucket = byOffer.get(l.offer_id);
      if (bucket) bucket.push(l);
      else byOffer.set(l.offer_id, [l]);
    }
    const geekOffers = sent.map((o) => ({
      ...toPipelineOffer(o),
      total_amount: calcOfferTotals(o, byOffer.get(o.id) ?? []).bf,
    }));
    return {
      value: weightedPipeline(geekOffers, weights, new Date()),
      offerCount: sent.length,
      nextCloseDate: nextCloseDate(sent),
      isLoading: offersLoading || (ids.length > 0 && linesLoading),
    };
  }, [sent, ids.length, lines, weights, offersLoading, linesLoading]);
}
